import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Logo } from "./Logo";

const NAV = [
  { label: "Customers", to: "/customers" },
  { label: "Businesses", to: "/business" },
  { label: "Discover", to: "/discover" },
  { label: "Rewards", to: "/rewards" },
  { label: "Restaurants", to: "/restaurants" },
  { label: "Enterprise", to: "/enterprise" },
  { label: "About", to: "/about" },
];

const getApp = () =>
  toast("MONOE is coming soon.", { description: "Launching on the App Store and Google Play. Thanks for your interest!" });

const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      data-testid="theme-toggle-button"
      aria-label="Toggle theme"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className="grid h-9 w-9 place-items-center rounded-md border border-border text-foreground transition-colors hover:bg-accent"
    >
      {mounted && isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
};

const navTestId = (label, prefix = "nav-link") => `${prefix}-${label.toLowerCase().replace(/\s+/g, "-")}`;

const DesktopNav = () => (
  <nav className="hidden items-center gap-1 md:flex">
    {NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === "/"}
        data-testid={navTestId(item.label)}
        className={({ isActive }) =>
          `rounded-md px-3 py-2 text-sm font-medium transition-colors hover:text-foreground ${
            isActive ? "text-foreground" : "text-muted-foreground"
          }`
        }
      >
        {item.label}
      </NavLink>
    ))}
  </nav>
);

const DesktopActions = () => (
  <div className="hidden items-center gap-2 md:flex">
    <ThemeToggle />
    <Link
      to="/business"
      data-testid="header-for-business-link"
      className="rounded-md border border-border px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
    >
      For business
    </Link>
    <button
      type="button"
      data-testid="header-get-app-button"
      onClick={getApp}
      className="rounded-md bg-foreground px-4 py-2 text-sm font-semibold text-background transition-transform duration-200 hover:-translate-y-0.5"
    >
      Get the app
    </button>
  </div>
);

const MobileTrigger = ({ open, onToggle }) => (
  <div className="flex items-center gap-2 md:hidden">
    <ThemeToggle />
    <button
      type="button"
      data-testid="mobile-menu-toggle"
      aria-label="Menu"
      onClick={onToggle}
      className="grid h-9 w-9 place-items-center rounded-md border border-border text-foreground"
    >
      {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
    </button>
  </div>
);

const MobileMenu = () => (
  <div data-testid="mobile-menu" className="border-t border-border bg-background md:hidden">
    <nav className="mx-auto flex max-w-7xl flex-col px-4 py-3 sm:px-6">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/"}
          data-testid={navTestId(item.label, "mobile-nav-link")}
          className={({ isActive }) =>
            `rounded-md px-3 py-3 text-base font-medium transition-colors ${
              isActive ? "text-foreground" : "text-muted-foreground"
            }`
          }
        >
          {item.label}
        </NavLink>
      ))}
      <button
        type="button"
        data-testid="mobile-get-app-button"
        onClick={getApp}
        className="mt-2 rounded-md bg-foreground px-4 py-3 text-center text-sm font-semibold text-background"
      >
        Get the app
      </button>
    </nav>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-border bg-background/80 backdrop-blur-md" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <DesktopNav />
        <DesktopActions />
        <MobileTrigger open={open} onToggle={() => setOpen((v) => !v)} />
      </div>

      {open && <MobileMenu />}
    </header>
  );
};
